/** shortcuts listener tools */

/** keys data map */
const keyMap = {};

/** shortcuts handling restriction flag */
let areShortcutsLocked = false;

/**
 * Add key clicked event handler
 * @param {String} keycode code value of the physical key
 * @param {Function} handler event handler
 * @param {Object} options subscribe options:
 * ctrl - is pressed Control key
 * once - need to remove handler after first handling
 * prevent - to prevent default key behaviour or not
 */
export const subscribeToKeydown = (keycode, handler, options = {}) => {
  subscribeToKey(keycode, handler, { ...options, keyup: false });
};

/**
 * Add key released event handler
 * @param {String} keycode code value of the physical key
 * @param {Function} handler event handler
 * @param {Object} options subscribe options:
 * ctrl - is pressed Control key
 * once - need to remove handler after first handling
 * prevent - to prevent default key behaviour or not
 */
export const subscribeToKeyup = (keycode, handler, options = {}) => {
  subscribeToKey(keycode, handler, { ...options, keyup: true });
};

/**
 * Stop shortcuts handling
 */
export const lockShortcuts = () => {
  areShortcutsLocked = true;
};

/**
 * Restart shortcuts handling
 */
export const unlockShortcuts = () => {
  areShortcutsLocked = false;
};

/**
 * Add handler for key event (keyup or keydown)
 * @param {String} keycode code value of the physical key
 * @param {Function} handler handler event handler
 * @param {Object} options subscribe options:
 * ctrl - is pressed Control key
 * once - need to remove handler after first handling
 * prevent - to prevent default key behaviour or not
 * keyup - is it's keyup or keydown event
 */
function subscribeToKey(
  keycode,
  handler,
  { 
    ctrl = false,
    once = false,
    prevent = false,
    keyup
  }
) {
  /** init keymap */
  if (!keyMap[keycode]) {
    keyMap[keycode] = {
      handlers: {
        keyup: [],
        keydown: []
      },
      /** is this key need to work in Control pressed mode */
      ctrl: false
    };
  }

  const listName = keyup ? "keyup" : "keydown";
  keyMap[keycode].handlers[listName].push({
    action: handler,
    ctrl: ctrl,
    once: once,
    prevent: prevent
  });
}

/**
 * Handle keydown event (only at first time, not for repeats)
 * @param {KeyboardEvent} event event object
 * @returns {Boolean} is key handled or not
 */
function handleKeydown(event) {
  const { code, ctrlKey, repeat } = event;
  /** shortcuts blocked */
  if (areShortcutsLocked || checkUserTyping() || repeat) return false;

  const keyData = keyMap[code];
  /** unsubscribed key */
  if (!keyData) return false;

  /** mark that key pressed with ctrl */
  if (ctrlKey) {
    keyData.ctrl = true;
  }

  launchHandlers(event, keyData.handlers.keydown, ctrlKey, event);
  return true;
}

/**
 * Handle keyup event
 * @param {KeyboardEvent} event event object
 * @returns {Boolean} is key handled or not
 */
function handleKeyup(event) {
  const { code, ctrlKey } = event;
  /** shortcuts blocked */
  if (areShortcutsLocked || checkUserTyping()) return false;

  const keyData = keyMap[code];

  /** unsubscribed key */
  if (!keyData) return false;

  launchHandlers(event, keyData.handlers.keyup, keyData.ctrl);

  if (!ctrlKey) {
    keyData.ctrl = false;
  }

  return true;
}

function launchHandlers(event, handlers, ctrlPressed) {
  const defaultPrevented = false;

  handlers.forEach(({ action, ctrl, once, prevent }, index, handlers) => {
    /** Ctrl+[key] works when [key] doesn't work */
    if (ctrl == ctrlPressed) {
      try {
        action();
      } catch (error) {
        console.error(error);
      }

      if (once) {
        handlers.splice(index, 1);
      }

      if (prevent && !defaultPrevented) {
        event.preventDefault();
      }
    }
  });
}

/**
 * Set listeners
 */
function init() {
  document.addEventListener("keydown", handleKeydown);
  document.addEventListener("keyup", handleKeyup);
}

/**
 * Check if user is typing something now on page
 */
function checkUserTyping() {
  const active = document.activeElement;
  const textTags = ["input", "textarea"];
  return textTags.includes(active.tagName.toLowerCase());
}

init();
